import { useEffect, useState, type CSSProperties, type ReactElement } from 'react';
import { MovableDialog } from '@/components/shared/MovableDialog';
import { v2, type SpecFileReview } from '@/api/v2Client';
import { formatApiError } from '@/lib/errors';

/**
 * T15-D1 — review a spec version BEFORE it is activated, and the ONLY place
 * activation happens. The server computes everything shown here (GET
 * /api/v1/spec-files/:id/review): the version currently in force in the
 * same slot and the section diff between the two. Activating sends back the
 * reviewed in-force id; the server refuses a missing or stale review.
 */
export interface SpecFileReviewPanelProps {
  readonly specFileId: string;
  readonly onClose: () => void;
  readonly onActivated: (result: {
    title: string;
    deactivated: string[];
    warning: string | null;
  }) => void;
}

export function SpecFileReviewPanel({
  specFileId,
  onClose,
  onActivated,
}: SpecFileReviewPanelProps): ReactElement {
  const [review, setReview] = useState<SpecFileReview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setReview(null);
    setError(null);
    v2.specFiles
      .review(specFileId)
      .then((r) => {
        if (!cancelled) setReview(r);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(formatApiError(err, 'Could not load the review.'));
      });
    return (): void => {
      cancelled = true;
    };
  }, [specFileId]);

  const activate = async (): Promise<void> => {
    if (!review) return;
    setBusy(true);
    setError(null);
    try {
      const res = await v2.specFiles.setActive(specFileId, true, review.reviewedActiveId);
      onActivated({ title: review.candidate.Title, deactivated: res.deactivated, warning: res.warning });
    } catch (err) {
      setError(formatApiError(err, 'Could not activate this version.'));
    } finally {
      setBusy(false);
    }
  };

  const diff = review?.diff;
  return (
    <MovableDialog
      persistKey="spec-file-review"
      title={review ? `Review before activating — ${review.candidate.Title}` : 'Review before activating'}
      onClose={busy ? undefined : onClose}
      testId="spec-file-review-panel"
      width={640}
    >
      <div style={{ fontSize: 13, display: 'grid', gap: 12 }}>
        {review === null && error === null && <div>Loading the review…</div>}
        {review && (
          <>
            <div data-testid="spec-file-review-versions">
              <div>
                Activating: <strong>{review.candidate.Title}</strong>
              </div>
              <div>
                In force now:{' '}
                {review.active ? (
                  <strong>{review.active.Title}</strong>
                ) : (
                  <em>no version is in force for this slot</em>
                )}
              </div>
            </div>
            {/* T15-D2 — the impact, stated by the server (one source). */}
            {review.impact && (
              <div data-testid="spec-file-review-impact" style={impactStyle}>
                {review.impact.statement}
              </div>
            )}
            {diff && !diff.comparable && (
              <div data-testid="spec-file-review-not-comparable" style={noteStyle}>
                Sections not compared: {diff.reason} The new file declares {diff.sectionsInFile}{' '}
                section{diff.sectionsInFile === 1 ? '' : 's'}.
              </div>
            )}
            {diff && diff.comparable && (
              <div data-testid="spec-file-review-diff" style={{ display: 'grid', gap: 8 }}>
                <div>
                  <strong>{diff.newSections?.length ?? 0}</strong> added ·{' '}
                  <strong>{diff.removedSections?.length ?? 0}</strong> removed ·{' '}
                  <strong>{diff.unchangedCount ?? 0}</strong> unchanged
                </div>
                {(diff.newSections?.length ?? 0) > 0 && (
                  <ul data-testid="spec-file-review-added" style={listStyle}>
                    {diff.newSections!.map((s) => (
                      <li key={`a-${s}`} style={{ color: 'var(--green)' }}>
                        + {s}
                      </li>
                    ))}
                  </ul>
                )}
                {(diff.removedSections?.length ?? 0) > 0 && (
                  <ul data-testid="spec-file-review-removed" style={listStyle}>
                    {diff.removedSections!.map((s) => (
                      <li key={`r-${s}`} style={{ color: 'var(--red)' }}>
                        − {s}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </>
        )}
        {error !== null && (
          <div role="alert" data-testid="spec-file-review-error" style={{ color: 'var(--red)' }}>
            {error}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" onClick={onClose} disabled={busy} style={secondaryBtn}>
            Close
          </button>
          {review && !review.candidate.IsActive && (
            <button
              type="button"
              data-testid="spec-file-review-activate"
              disabled={busy}
              onClick={() => void activate()}
              style={primaryBtn}
            >
              {busy ? 'Activating…' : `Activate ${review.candidate.Title}`}
            </button>
          )}
        </div>
      </div>
    </MovableDialog>
  );
}

const impactStyle: CSSProperties = {
  padding: '8px 10px',
  borderRadius: 6,
  background: '#eff6ff',
  border: '1px solid #93c5fd',
  color: '#1e3a8a',
  lineHeight: 1.5,
};
const noteStyle: CSSProperties = {
  padding: '8px 10px',
  borderRadius: 6,
  background: '#fef3c7',
  border: '1px solid #fcd34d',
  color: '#78350f',
};
const listStyle: CSSProperties = {
  margin: 0,
  paddingLeft: 18,
  maxHeight: 160,
  overflowY: 'auto',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
};
const secondaryBtn: CSSProperties = {
  padding: '6px 14px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  background: 'none',
  cursor: 'pointer',
};
const primaryBtn: CSSProperties = {
  padding: '6px 14px',
  border: '1px solid var(--accent)',
  borderRadius: 6,
  background: 'var(--accent)',
  color: '#fff',
  fontWeight: 600,
  cursor: 'pointer',
};
